import { useEffect, useMemo, useRef, useState } from 'react'

import { type ContactSectionMeta } from '#app/routes/mocra+/_form+/adverse-events/contact-sections.ts'
import { type AdverseEventContactEntry } from '#app/routes/mocra+/_form+/adverse-events/form.ts'
import { ADVERSE_EVENTS_SECTION_RESOURCES } from '#app/routes/mocra+/_form+/adverse-events/section-resources.ts'
import { ContactAdd } from '#app/routes/mocra+/_form+/components/contacts/add.tsx'
import { ContactCard } from '#app/routes/mocra+/_form+/components/contacts/card.tsx'
import { ContactEditOverlay } from '#app/routes/mocra+/_form+/components/contacts/edit.tsx'
import {
	createEmptyContactDraft,
	type ContactCandidate,
	type ContactDraft,
} from '#app/routes/mocra+/_form+/components/contacts/types.ts'
import { combineCandidateLists } from '#app/routes/mocra+/_form+/components/contacts/utils.ts'
import { Section } from '#app/routes/mocra+/_form+/components/section.tsx'
import {
	type ContactDirectoryEntry,
	type ContactInput,
} from '#app/routes/mocra+/_form+/contact-directory.ts'
import { type EntityCollectionItem } from '#app/routes/mocra+/_form+/entity-definition.ts'
import { useContactDirectory } from '#app/routes/mocra+/_form+/hooks/use-contact-directory.ts'
import { type SectionResources } from '#app/routes/mocra+/_form+/section-resources.ts'

type ContactCollectionProps = {
	readonly title: string
	readonly description: string
	readonly emptyState: string
	readonly defaultRole: string
	readonly items: readonly EntityCollectionItem<
		AdverseEventContactEntry,
		ContactDraft
	>[]
	readonly onAdd: (draft: ContactDraft) => void
	readonly onEdit: (index: number, draft: ContactDraft) => void
	readonly onRemove: (index: number) => void
	readonly listErrors: readonly string[]
	readonly directorySource?: string
	readonly candidates?: readonly ContactCandidate[]
	readonly resources?: SectionResources
}

type Props = {
	readonly items: readonly EntityCollectionItem<
		AdverseEventContactEntry,
		ContactDraft
	>[]
	readonly onAdd: (draft: ContactDraft) => void
	readonly onEdit: (index: number, draft: ContactDraft) => void
	readonly onRemove: (index: number) => void
	readonly section: ContactSectionMeta
	readonly defaultRole: string
	readonly listErrors: readonly string[]
	readonly candidates?: readonly ContactCandidate[]
	readonly resources?: SectionResources
}

type ContactDisplay = {
	readonly contactId: string
	readonly role: string
	readonly name: string
	readonly email: string
	readonly phone: string
}

export function ContactCollectionSection({
	title,
	description,
	emptyState,
	defaultRole,
	items,
	onAdd,
	onEdit,
	onRemove,
	listErrors,
	directorySource,
	candidates = [],
	resources = ADVERSE_EVENTS_SECTION_RESOURCES,
}: ContactCollectionProps) {
	const {
		contacts: directoryEntries,
		candidates: directoryCandidates,
		mergeContacts,
	} = useContactDirectory()

	const directoryById = useMemo(() => {
		const map = new Map<string, ContactDirectoryEntry>()
		for (const entry of directoryEntries) {
			map.set(entry.id, entry)
		}
		return map
	}, [directoryEntries])

	const displays = useMemo(() => {
		return items.map((item) =>
			resolveDisplay(item.entry, directoryById, defaultRole),
		)
	}, [items, directoryById, defaultRole])

	const existingDrafts = useMemo(
		() => items.map((item) => sanitizeDraft(item.draft, defaultRole)),
		[defaultRole, items],
	)

	const mergedCandidates = useMemo(
		() => combineCandidateLists(directoryCandidates, candidates),
		[directoryCandidates, candidates],
	)

	const directoryLabel = directorySource ?? title

	const directoryInputs = useMemo(
		() =>
			items
				.map((item) => toDirectoryInput(item.entry, defaultRole))
				.filter((input): input is ContactInput => input !== null),
		[defaultRole, items],
	)

	const mergeSignatureRef = useRef<string | null>(null)
	useEffect(() => {
		if (directoryInputs.length === 0) {
			mergeSignatureRef.current = null
			return
		}
		const signature = directoryInputs.map(contactInputSignature).join('||')
		if (mergeSignatureRef.current === signature) return
		mergeSignatureRef.current = signature
		mergeContacts(directoryInputs, { source: directoryLabel })
	}, [directoryInputs, mergeContacts, directoryLabel])

	const [editingIndex, setEditingIndex] = useState<number | null>(null)
	const editingDraft =
		editingIndex !== null
			? (existingDrafts[editingIndex] ?? createEmptyContactDraft())
			: createEmptyContactDraft()

	const handleAdd = (draft: ContactDraft) => {
		onAdd(draft)
	}

	const handleEdit = (draft: ContactDraft) => {
		if (editingIndex === null) return
		onEdit(editingIndex, draft)
		setEditingIndex(null)
	}

	const handleRemove = (index: number) => {
		onRemove(index)
		setEditingIndex(null)
	}

	const hasContacts = items.length > 0

	return (
		<Section
			title={title}
			description={description}
			mocraSections={resources.mocraSections}
			templates={resources.templates}
			childrenClassName="space-y-4"
			meta={
				listErrors.length ? (
					<ul className="text-destructive list-disc space-y-1 pl-5 text-xs">
						{listErrors.map((warning) => (
							<li key={warning}>{warning}</li>
						))}
					</ul>
				) : null
			}
		>
			<div className="space-y-3">
				{hasContacts ? (
					items.map((item, index) => {
						const contact = item.entry
						const display =
							displays[index] ??
							resolveDisplay(contact, directoryById, defaultRole)
						const errors = item.errors ?? []
						const hasErrors = errors.length > 0

						return (
							<ContactCard
								key={`${display.contactId || display.email}-${index}`}
								name={display.name}
								title={display.role}
								email={display.email}
								phone={display.phone}
								contextLabel={title}
								onClick={() => setEditingIndex(index)}
								onRemove={() => handleRemove(index)}
								removeLabel="Remove"
								removeConfirmTitle="Remove this contact?"
								removeConfirmDescription="This contact will be removed from the list."
								className={
									hasErrors
										? 'border-destructive/40 ring-destructive/40 ring-1'
										: undefined
								}
							/>
						)
					})
				) : (
					<p className="text-muted-foreground text-sm">{emptyState}</p>
				)}
			</div>
			<ContactAdd
				onSubmit={handleAdd}
				candidates={mergedCandidates}
				directorySource={directoryLabel}
				existingContacts={existingDrafts}
			/>
			<ContactEditOverlay
				open={editingIndex !== null}
				onOpenChange={(open) => {
					if (!open) {
						setEditingIndex(null)
					}
				}}
				onSubmit={handleEdit}
				initialValue={editingDraft}
				candidates={mergedCandidates}
				directorySource={directoryLabel}
				existingContacts={
					editingIndex === null
						? existingDrafts
						: existingDrafts.filter((_, idx) => idx !== editingIndex)
				}
				onClosed={() => setEditingIndex(null)}
				onCancel={() => setEditingIndex(null)}
			/>
		</Section>
	)
}

export function AdverseEventContactsSection({
	items,
	onAdd,
	onEdit,
	onRemove,
	section,
	defaultRole,
	listErrors,
	candidates = [],
	resources,
}: Props) {
	return (
		<ContactCollectionSection
			items={items}
			onAdd={onAdd}
			onEdit={onEdit}
			onRemove={onRemove}
			listErrors={listErrors}
			title={section.title}
			description={section.description}
			emptyState={section.emptyState}
			defaultRole={defaultRole}
			directorySource={section.title}
			candidates={candidates}
			resources={resources}
		/>
	)
}

function sanitizeDraft(draft: ContactDraft, defaultRole: string): ContactDraft {
	return {
		id: draft.id ? draft.id.trim() : undefined,
		title: draft.title?.trim() || defaultRole,
		name: draft.name.trim(),
		email: draft.email.trim(),
		phone: draft.phone?.trim() ?? '',
	}
}

function resolveDisplay(
	contact: AdverseEventContactEntry,
	directory: Map<string, ContactDirectoryEntry>,
	defaultRole: string,
): ContactDisplay {
	const entry = contact.contactId ? directory.get(contact.contactId) : undefined
	const role = contact.role?.trim() || defaultRole
	const name =
		entry?.name ??
		toOptionalString(contact.fallbackName) ??
		toOptionalString(contact.name) ??
		role
	const email =
		entry?.email ??
		toOptionalEmail(contact.fallbackEmail) ??
		toOptionalEmail(contact.email) ??
		''
	const phone =
		entry?.phone ??
		toOptionalString(contact.fallbackPhone) ??
		toOptionalString(contact.phone) ??
		''

	return {
		contactId: contact.contactId?.trim() ?? '',
		role,
		name,
		email,
		phone,
	}
}

function toDirectoryInput(
	contact: AdverseEventContactEntry,
	defaultRole: string,
): ContactInput | null {
	const id = toOptionalString(contact.contactId)
	const email =
		toOptionalEmail(contact.fallbackEmail) ?? toOptionalEmail(contact.email)
	if (!id || !email) return null
	return {
		id,
		name:
			toOptionalString(contact.fallbackName) ??
			toOptionalString(contact.name) ??
			email,
		email,
		title:
			toOptionalString(contact.fallbackTitle) ??
			toOptionalString(contact.role) ??
			defaultRole,
		phone:
			toOptionalString(contact.fallbackPhone) ??
			toOptionalString(contact.phone) ??
			'',
	}
}

function contactInputSignature(input: ContactInput): string {
	const id =
		'id' in input ? (toOptionalString((input as { id?: string }).id) ?? '') : ''
	const email =
		'email' in input
			? (toOptionalEmail((input as { email?: string }).email) ?? '')
			: ''
	const name =
		'name' in input
			? (toOptionalString((input as { name?: string }).name) ?? '')
			: ''
	const phone =
		'phone' in input
			? (toOptionalString((input as { phone?: string }).phone) ?? '')
			: ''
	const title =
		'title' in input && typeof input.title === 'string'
			? (toOptionalString(input.title) ?? '')
			: 'source' in input && typeof input.source === 'string'
				? (toOptionalString(input.source) ?? '')
				: ''
	return `${id}|${email}|${name}|${phone}|${title}`
}

function toOptionalString(value: unknown): string | undefined {
	if (typeof value !== 'string') return undefined
	const trimmed = value.trim()
	return trimmed.length > 0 ? trimmed : undefined
}

function toOptionalEmail(value: unknown): string | undefined {
	const trimmed = toOptionalString(value)
	if (!trimmed) return undefined
	return trimmed
}
